import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, DownloadSimple, CheckCircle } from '@phosphor-icons/react';
import ClientAutocomplete from '../components/ClientAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    broj_fakture: '',
    datum: new Date().toISOString().split('T')[0],
    mjesec_usluge: '',
    iznos: 0,
    pdv: 0,
    ukupno: 0,
    napomena: '',
  });

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, []);

  useEffect(() => {
    const ukupno = formData.iznos + formData.pdv;
    setFormData((prev) => ({ ...prev, ukupno }));
  }, [formData.iznos, formData.pdv]);

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/invoices');
      setInvoices(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju faktura');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju klijenata');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const invoiceData = {
        ...formData,
        datum: new Date(formData.datum).toISOString(),
      };
      await api.post('/invoices', invoiceData);
      toast.success('Faktura kreirana');
      setIsDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri kreiranju fakture');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}/pay`);
      toast.success('Faktura označena kao plaćena');
      fetchInvoices();
    } catch (error) {
      toast.error('Greška pri ažuriranju fakture');
    }
  };

  const handleDownloadPDF = async (invoiceId, brojFakture) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${BACKEND_URL}/api/invoices/${invoiceId}/pdf`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `faktura_${brojFakture}.pdf`;
      a.click();
      toast.success('PDF preuzet');
    } catch (error) {
      toast.error('Greška pri preuzimanju PDF-a');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      broj_fakture: '',
      datum: new Date().toISOString().split('T')[0],
      mjesec_usluge: '',
      iznos: 0,
      pdv: 0,
      ukupno: 0,
      napomena: '',
    });
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Fakture</h3>
          <p className="text-sm text-slate-600 mt-1">Upravljanje fakturama i plaćanjima</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
              data-testid="invoices-add-button"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Kreiraj fakturu
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="invoice-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium text-slate-900">Kreiraj novu fakturu</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="invoice-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent *</Label>
                <ClientAutocomplete clients={clients} value={formData.client_id} onChange={(id) => setFormData({ ...formData, client_id: id })} testid="invoice-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Broj fakture *</Label>
                  <Input
                    required
                    value={formData.broj_fakture}
                    onChange={(e) => setFormData({ ...formData, broj_fakture: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="invoice-broj-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Datum *</Label>
                  <DateInput
                    value={formData.datum}
                    onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                    className="border-slate-300 text-sm"
                    data-testid="invoice-datum-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Mjesec usluge *</Label>
                <Input
                  required
                  value={formData.mjesec_usluge}
                  onChange={(e) => setFormData({ ...formData, mjesec_usluge: e.target.value })}
                  className="bg-white border-slate-300 text-sm"
                  placeholder="npr. Januar 2026"
                  data-testid="invoice-mjesec-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Iznos (€) *</Label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    value={formData.iznos}
                    onChange={(e) => setFormData({ ...formData, iznos: parseFloat(e.target.value) || 0 })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="invoice-iznos-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">PDV (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.pdv}
                    onChange={(e) => setFormData({ ...formData, pdv: parseFloat(e.target.value) || 0 })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="invoice-pdv-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Ukupno (€)</Label>
                <Input
                  disabled
                  value={formData.ukupno.toFixed(2)}
                  className="bg-slate-100 border-slate-300 text-sm font-semibold"
                  data-testid="invoice-ukupno-display"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Napomena</Label>
                <Input
                  value={formData.napomena}
                  onChange={(e) => setFormData({ ...formData, napomena: e.target.value })}
                  className="bg-white border-slate-300 text-sm"
                  data-testid="invoice-napomena-input"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-md px-4 py-2 font-medium"
                  data-testid="invoice-cancel-button"
                >
                  Odustani
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                  data-testid="invoice-submit-button"
                >
                  Kreiraj
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Učitavanje...</p>
        </div>
      ) : invoices.length === 0 ? (
        <Card className="p-8 text-center" data-testid="invoices-empty-state">
          <p className="text-slate-500">Nema faktura</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600" data-testid="invoices-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Broj</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Datum</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Mjesec usluge</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Ukupno</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Status</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const client = clients.find((c) => c.id === invoice.client_id);
                  return (
                    <tr key={invoice.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`invoice-row-${invoice.id}`}>
                      <td className="px-5 py-3 font-medium" data-testid={`invoice-broj-${invoice.id}`}>{invoice.broj_fakture}</td>
                      <td className="px-5 py-3">{client?.skraceni_naziv || client?.naziv_firme || 'N/A'}</td>
                      <td className="px-5 py-3">{formatDate(invoice.datum)}</td>
                      <td className="px-5 py-3">{invoice.mjesec_usluge}</td>
                      <td className="px-5 py-3 font-semibold" data-testid={`invoice-ukupno-${invoice.id}`}>{invoice.ukupno.toFixed(2)} €</td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            invoice.placeno
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}
                          data-testid={`invoice-status-${invoice.id}`}
                        >
                          {invoice.placeno ? 'Plaćeno' : 'Neplaćeno'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadPDF(invoice.id, invoice.broj_fakture)}
                            className="p-2 hover:bg-slate-200 rounded transition-colors"
                            title="Preuzmi PDF"
                            data-testid={`invoice-download-${invoice.id}`}
                          >
                            <DownloadSimple size={16} className="text-slate-600" />
                          </button>
                          {!invoice.placeno && (
                            <button
                              onClick={() => handleMarkPaid(invoice.id)}
                              className="p-2 hover:bg-emerald-100 rounded transition-colors"
                              title="Označi kao plaćeno"
                              data-testid={`invoice-mark-paid-${invoice.id}`}
                            >
                              <CheckCircle size={16} className="text-emerald-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
